// The workspace document's operations (slice 05, issue 745): presets, the
// named drop-downs and the two resets, as pure functions over the document
// vtt/shared defines. Nothing here touches the dock or the network; the shell
// calls these, then persists the result and tells the dock what changed.
import {
  FIGHT_PRESET_ID, STOCK_PRESET_ID, shippedDock, type CustomMenu, type DockLayout, type LayoutDoc, type Preset, type TabIconId, type ViewId, VIEWS,
} from '@dragons-table/vtt-shared';

export const STOCK_PRESET_NAME = 'Stock layout';
export const FIGHT_PRESET_NAME = 'Fight';

/**
 * The Fight layout (qa-runs#404, Tom's decision 1): Properties on the left, the full turn tracker on the right, the
 * initiative strip at the bottom. The main window only — the second window keeps whatever it holds; the dice bag is
 * reached from the Dice menu or kept there. Shipped, so it is never stored: changed and saved, it is the person's own.
 */
export const FIGHT_PRESET: Preset = {
  id: FIGHT_PRESET_ID,
  name: FIGHT_PRESET_NAME,
  views: { main: shippedDock({ left: ['properties'], right: ['tracker'], bottom: ['initiative'] }) },
};

const SHIPPED_NAMES = [STOCK_PRESET_NAME, FIGHT_PRESET_NAME];
const shippedName = (name: string) => SHIPPED_NAMES.some((n) => sameName(n, name));

const newId = (prefix: string): string =>
  `${prefix}-${Date.now().toString(36)}${Math.floor(Math.random() * 1e6).toString(36)}`;

const clone = <T>(v: T): T => (v === null || v === undefined ? v : (JSON.parse(JSON.stringify(v)) as T));

const sameName = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/** A tool's tab icon picked, or put back to its name with `null` (qa-runs#404): kept per person per label, as the arrangement. */
export function withIcon(doc: LayoutDoc, tool: string, icon: TabIconId | null): LayoutDoc {
  const icons = { ...(doc.icons ?? {}) };
  if (icon) icons[tool] = icon; else delete icons[tool];
  const { icons: _old, ...rest } = doc;
  return Object.keys(icons).length > 0 ? { ...rest, icons } : rest;
}

/** The presets the person can switch to: the stock one first, the Fight layout, then theirs. */
export function listPresets(doc: LayoutDoc): Preset[] {
  return [{ id: STOCK_PRESET_ID, name: STOCK_PRESET_NAME, views: { main: null, second: null } }, clone(FIGHT_PRESET), ...doc.presets];
}

export function presetById(doc: LayoutDoc, id: string): Preset | null {
  return listPresets(doc).find((p) => p.id === id) ?? null;
}

/** Which views a preset claims, for the row that says "main window", "second window" or "both". */
export function coverageOf(preset: Preset): ViewId[] {
  return VIEWS.filter((v) => v in preset.views);
}

/**
 * Save an arrangement under a name. A name already in use is REPLACED (the
 * dialog says so); the stock preset's name is refused, as is an empty set of
 * views. Returns the preset stored.
 */
export function savePreset(doc: LayoutDoc, name: string, views: Partial<Record<ViewId, DockLayout | null>>): { doc: LayoutDoc; preset: Preset } | null {
  const trimmed = name.trim();
  if (!trimmed || shippedName(trimmed) || Object.keys(views).length === 0) return null;
  const existing = doc.presets.find((p) => sameName(p.name, trimmed));
  const preset: Preset = { id: existing?.id ?? newId('p'), name: trimmed, views: clone(views) };
  const presets = existing ? doc.presets.map((p) => (p.id === preset.id ? preset : p)) : [...doc.presets, preset];
  // The views just saved are now "set from" this preset, so "reset this preset" returns here.
  const current = { ...doc.current };
  for (const view of coverageOf(preset)) current[view] = { dock: clone(views[view] ?? null), preset: preset.id };
  return { doc: { ...doc, presets, current }, preset };
}

/** Switch to a preset: only the views it covers change (design/07 §7). */
export function applyPreset(doc: LayoutDoc, presetId: string): LayoutDoc {
  const preset = presetById(doc, presetId);
  if (!preset) return doc;
  const current = { ...doc.current };
  for (const view of coverageOf(preset)) current[view] = { dock: clone(preset.views[view] ?? null), preset: preset.id };
  return { ...doc, current };
}

/** "Reset this preset": the view back to how the preset it was set from was saved; stock if that preset is gone or does not cover it. */
export function resetView(doc: LayoutDoc, view: ViewId): LayoutDoc {
  const preset = presetById(doc, doc.current[view].preset);
  const dock = preset && view in preset.views ? clone(preset.views[view] ?? null) : null;
  return { ...doc, current: { ...doc.current, [view]: { dock, preset: preset && view in preset.views ? preset.id : STOCK_PRESET_ID } } };
}

export function renamePreset(doc: LayoutDoc, id: string, name: string): LayoutDoc | null {
  const trimmed = name.trim();
  if (!trimmed || shippedName(trimmed)) return null;
  if (doc.presets.some((p) => p.id !== id && sameName(p.name, trimmed))) return null;
  if (!doc.presets.some((p) => p.id === id)) return null;
  return { ...doc, presets: doc.presets.map((p) => (p.id === id ? { ...p, name: trimmed } : p)) };
}

/** Delete a preset; a view set from it keeps its arrangement and is now "from stock" for the purpose of a reset. */
export function deletePreset(doc: LayoutDoc, id: string): LayoutDoc {
  const current = { ...doc.current };
  for (const view of VIEWS) if (current[view].preset === id) current[view] = { ...current[view], preset: STOCK_PRESET_ID };
  return { ...doc, presets: doc.presets.filter((p) => p.id !== id), current };
}

export function addMenu(doc: LayoutDoc, name: string): { doc: LayoutDoc; menu: CustomMenu } | null {
  const trimmed = name.trim();
  if (!trimmed || doc.menus.some((m) => sameName(m.name, trimmed))) return null;
  const menu: CustomMenu = { id: newId('m'), name: trimmed, tools: [] };
  return { doc: { ...doc, menus: [...doc.menus, menu] }, menu };
}

export function renameMenu(doc: LayoutDoc, id: string, name: string): LayoutDoc | null {
  const trimmed = name.trim();
  if (!trimmed || doc.menus.some((m) => m.id !== id && sameName(m.name, trimmed))) return null;
  if (!doc.menus.some((m) => m.id === id)) return null;
  return { ...doc, menus: doc.menus.map((m) => (m.id === id ? { ...m, name: trimmed } : m)) };
}

export function removeMenu(doc: LayoutDoc, id: string): LayoutDoc {
  return { ...doc, menus: doc.menus.filter((m) => m.id !== id) };
}

export function addToolToMenu(doc: LayoutDoc, menuId: string, tool: string): LayoutDoc {
  return {
    ...doc,
    menus: doc.menus.map((m) => (m.id === menuId && !m.tools.includes(tool) ? { ...m, tools: [...m.tools, tool] } : m)),
  };
}

export function removeToolFromMenu(doc: LayoutDoc, menuId: string, tool: string): LayoutDoc {
  return { ...doc, menus: doc.menus.map((m) => (m.id === menuId ? { ...m, tools: m.tools.filter((t) => t !== tool) } : m)) };
}

/** The menus a tool is in, for the placement dialog. */
export function menusHolding(doc: LayoutDoc, tool: string): CustomMenu[] {
  return doc.menus.filter((m) => m.tools.includes(tool));
}

/** What a view shows now, as the dock should load it. */
export function currentDock(doc: LayoutDoc, view: ViewId): DockLayout | null {
  return doc.current[view].dock;
}

export function withDock(doc: LayoutDoc, view: ViewId, dock: DockLayout | null): LayoutDoc {
  return { ...doc, current: { ...doc.current, [view]: { ...doc.current[view], dock } } };
}
